<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { Eye, Plus, RotateCcw, Search } from '@lucide/vue'
import {
  Badge, Button, EmptyState, FormField, Input, Modal, Pagination, Section, Select, Spinner, Table, Toolbar, useToast,
} from '@drama/ui'

import { createEntity, listEntities } from '../../api/entity-library.js'

const ENTITY_TYPES = ['npc', 'player', 'scene', 'prop', 'effect']

const { t } = useI18n()
const toast = useToast()
const router = useRouter()

const loading = ref(false)
const error = ref('')
const items = ref([])
const total = ref(0)
const page = ref(1)
const pages = ref(1)
const pageSize = ref(50)
const filters = reactive({ type: '', q: '' })

const typeOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.entityLibrary.filters.allTypes') },
  ...ENTITY_TYPES.map(type => ({ value: type, label: t(`visualGameAdmin.entityLibrary.type.${type}`) })),
])

const columns = computed(() => [
  { key: 'image', label: t('visualGameAdmin.entityLibrary.table.image'), width: '72px' },
  { key: 'type', label: t('visualGameAdmin.entityLibrary.table.type'), width: '100px' },
  { key: 'canonical_name', label: t('visualGameAdmin.entityLibrary.table.canonicalName'), width: '200px' },
  { key: 'aliases', label: t('visualGameAdmin.entityLibrary.table.aliases'), width: '240px' },
  { key: 'status', label: t('visualGameAdmin.entityLibrary.table.status'), width: '100px' },
  { key: 'source', label: t('visualGameAdmin.entityLibrary.table.source'), width: '120px' },
  { key: 'actions', label: t('visualGameAdmin.common.actions'), width: '100px', align: 'right', fixed: 'right' },
])

const summary = computed(() => t('visualGameAdmin.assetLibrary.pager.summary', { total: total.value, page: page.value, pages: pages.value }))

onMounted(load)

async function load() {
  loading.value = true
  error.value = ''
  try {
    const data = await listEntities({
      type: filters.type || undefined,
      q: filters.q.trim() || undefined,
      page: page.value,
      limit: pageSize.value,
    })
    items.value = data.items
    total.value = data.total
    pages.value = data.pages
    page.value = data.page
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

function applyFilters() { page.value = 1; load() }
function resetFilters() { filters.type = ''; filters.q = ''; applyFilters() }
function prevPage() { page.value -= 1; load() }
function nextPage() { page.value += 1; load() }
function changePageSize(size) { pageSize.value = size; applyFilters() }

function openDetail(row) {
  router.push({ name: 'entity-library-detail', params: { entityId: row.id } })
}

// ------------------------------------------------------------------ 新建

const formOpen = ref(false)
const saving = ref(false)
const form = reactive({ type: 'npc', canonical_name: '', aliases: '' })
const formValid = computed(() => form.canonical_name.trim() !== '')

function openCreate() {
  form.type = 'npc'
  form.canonical_name = ''
  form.aliases = ''
  formOpen.value = true
}

async function submitForm() {
  saving.value = true
  try {
    const aliases = form.aliases.split(/[,，]/).map(s => s.trim()).filter(Boolean)
    const { entity } = await createEntity({
      type: form.type,
      canonical_name: form.canonical_name.trim(),
      aliases: aliases.length > 0 ? aliases : null,
    })
    formOpen.value = false
    toast.success(t('visualGameAdmin.entityLibrary.created'))
    openDetail(entity)
  } catch (requestError) {
    toast.error(requestError.message, { duration: 0 })
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <div class="elp vga-page" data-insp-path="apps/visual-game-admin/src/pages/entity-library/EntityLibraryPage.vue">
    <Section>
      <Toolbar>
        <div class="drama-toolbar-field drama-toolbar-field--wide">
          <label>{{ t('visualGameAdmin.assetLibrary.filters.search') }}</label>
          <Input v-model="filters.q" type="search" size="sm" :placeholder="t('visualGameAdmin.entityLibrary.filters.searchPlaceholder')" @keyup.enter="applyFilters" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.entityLibrary.table.type') }}</label>
          <Select v-model="filters.type" :options="typeOptions" size="sm" />
        </div>
        <template #actions>
          <Button size="sm" :loading="loading" @click="applyFilters">
            <Search :size="14" /> {{ t('visualGameAdmin.assetLibrary.filters.apply') }}
          </Button>
          <Button variant="ghost" size="sm" :disabled="loading" @click="resetFilters">
            <RotateCcw :size="14" /> {{ t('visualGameAdmin.common.reset') }}
          </Button>
          <Button size="sm" @click="openCreate">
            <Plus :size="14" /> {{ t('visualGameAdmin.entityLibrary.create') }}
          </Button>
        </template>
      </Toolbar>
    </Section>

    <div v-if="loading" class="vga-state"><Spinner /><span>{{ t('visualGameAdmin.common.loading') }}</span></div>
    <div v-else-if="error" class="vga-state">
      <p class="vga-error">{{ error }}</p>
      <Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button>
    </div>
    <template v-else>
      <Section :padded="false">
        <EmptyState v-if="items.length === 0" :title="t('visualGameAdmin.entityLibrary.emptyTitle')" />
        <Table v-else :columns="columns" :rows="items" clickable @row-click="openDetail">
          <template #image="{ row }">
            <img v-if="row.default_image_url" :src="row.default_image_url" class="elp__thumb" :alt="row.canonical_name" loading="lazy" />
            <div v-else class="elp__thumb elp__thumb--empty" aria-hidden="true" />
          </template>
          <template #type="{ row }"><Badge size="sm">{{ t(`visualGameAdmin.entityLibrary.type.${row.type}`) }}</Badge></template>
          <template #aliases="{ row }">{{ (row.aliases || []).join('、') || '—' }}</template>
          <template #status="{ row }"><Badge size="sm">{{ row.status }}</Badge></template>
          <template #source="{ row }">{{ row.source }}</template>
          <template #actions="{ row }">
            <Button variant="secondary" size="sm" @click.stop="openDetail(row)">
              <Eye :size="14" /> {{ t('visualGameAdmin.common.view') }}
            </Button>
          </template>
        </Table>
      </Section>
      <Pagination
        :has-prev="page > 1" :has-next="page < pages" :loading="loading" :summary="summary"
        :prev-label="t('visualGameAdmin.assetLibrary.pager.prev')" :next-label="t('visualGameAdmin.assetLibrary.pager.next')"
        :page-size="pageSize" :page-size-options="[20, 50, 100]" :page-size-label="t('visualGameAdmin.assetLibrary.pager.pageSize')"
        @prev="prevPage" @next="nextPage" @update:page-size="changePageSize"
      />
    </template>

    <Modal :open="formOpen" :title="t('visualGameAdmin.entityLibrary.create')" :close-on-backdrop="!saving" width="480px" @update:open="formOpen = $event">
      <form class="elp__form" @submit.prevent="submitForm">
        <FormField :label="t('visualGameAdmin.entityLibrary.table.type')" required>
          <Select v-model="form.type" :options="ENTITY_TYPES.map(type => ({ value: type, label: t(`visualGameAdmin.entityLibrary.type.${type}`) }))" block />
        </FormField>
        <FormField :label="t('visualGameAdmin.entityLibrary.table.canonicalName')" required>
          <Input v-model="form.canonical_name" :maxlength="200" block required />
        </FormField>
        <FormField :label="t('visualGameAdmin.entityLibrary.table.aliases')" :hint="t('visualGameAdmin.entityLibrary.form.aliasesHint')">
          <Input v-model="form.aliases" block />
        </FormField>
      </form>
      <template #footer>
        <Button variant="ghost" :disabled="saving" @click="formOpen = false">{{ t('visualGameAdmin.common.cancel') }}</Button>
        <Button variant="primary" :loading="saving" :disabled="!formValid" @click="submitForm">{{ t('visualGameAdmin.common.save') }}</Button>
      </template>
    </Modal>
  </div>
</template>

<style scoped>
.elp__form { display: grid; gap: var(--drama-space-md); }
.elp__thumb { width: 40px; height: 40px; object-fit: cover; border-radius: var(--drama-radius-sm); border: 1px solid var(--drama-color-border); display: block; }
.elp__thumb--empty { background: var(--drama-color-surface-alt); }
</style>
